import React, {
  useEffect,
  useRef,
  useState,
  useContext,
  useCallback,
} from 'react';
import styled from 'styled-components';
import { Buffer } from 'buffer';
import { Alert, AlertTitle } from '@mui/material';
import PropTypes from 'prop-types';
import { AppContext } from '../../../providers/authProvider';
import { LogicaContext } from '../../../providers/authLogicaProvider';
import api from '../../../api/req';
import init from './eusign';
import { Loader } from '../../../components/Styled/Misc';
import LogicaPing from '../Ping';

//= ================================================================
// Ідентифікатор батківського елементу для відображення iframe,
// який завантажує сторінку SignWidget

const SIGN_WIDGET_PARENT_ID = 'sign-widget-parent';
// Ідентифікатор iframe, який завантажує сторінку SignWidget

const SIGN_WIDGET_ID = 'sign-widget';

// URI з адресою за якою розташована сторінка SignWidget
// const SIGN_WIDGET_URI = 'https://certs.ciat.net.ua/'; // IIT GATEWAY
// const SIGN_WIDGET_URI = 'https://certs2.ciat.net.ua/'; // LOGICA GATEWAY

const Button = styled.button`
  height: 40px;
  cursor: pointer;

  :hover {
    color: white;
  }`;

function Authentication({ widgetUri }) {
  const EndUserRef = useRef(null);
  const EuSignRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [logicaPingSuccess, setLogicaPingSuccess] = useState(true);

  const { auth } = useContext(AppContext);
  const {
    ticket,
    setTicket,
  } = useContext(LogicaContext);

  const inputRef = useRef();
  const setInputFocus = useCallback(
    () => inputRef.current && inputRef.current.focus(),
    [],
  );

  const handleEnterClick = useCallback(
    () => setInputFocus(),
    [setInputFocus],
  );

  useEffect(() => {
    EndUserRef.current = init();

    // eslint-disable-next-line new-cap
    EuSignRef.current = new EndUserRef.current(
      SIGN_WIDGET_PARENT_ID,
      SIGN_WIDGET_ID,
      widgetUri,
      EndUserRef.current.FormType.ReadPKey,
    );

    //= ============================================================================

    // Створення об'єкту типу endUser для взаємодії з iframe,
    // який завантажує сторінку SignWidget

    EuSignRef.current
      .ReadPrivateKey()
      .then(() => {
        document.getElementById('sign-widget-parent').style.display = 'none';
        document.getElementById('sign-data-block').style.display = 'block';
        setInputFocus();
      })
      .catch((e) => {
        setError(`Виникла помилка при зчитуванні ос. ключа. Опис помилки: ${e.message}`);
      });

    document.body.addEventListener('click', handleEnterClick);

    return () => {
      document.body.removeEventListener('click', handleEnterClick);
    };
  }, [handleEnterClick, setInputFocus, widgetUri]);

  //= ============================================================================
  // Накладання підпису на дані

  const onSign = useCallback(
    async (strSign) => {
      function uint8array(string) {
        return Buffer.from(string, 'utf16le');
      }
      const external = false;
      const asBase64String = true;
      const signAlgo = EndUserRef.current.SignAlgo.DSTU4145WithGOST34311;
      const signType = EndUserRef.current.SignType.CAdES_X_Long;

      return (
        EuSignRef.current
          .SignData(uint8array(strSign), external, asBase64String, signAlgo, null, signType)
      );
    },
    [],
  );

  const processHandler = useCallback(() => {
    const getTicketClick = async (signBase64, requestId) => {
      const body = {
        requestId,
        signBase64,
        userAgent: 'Test',
      };
      const response = await api.post$('/api/logica/login/', auth, body);
      if (!response.ok && response.status === 422) {
        const errData = await response.json();
        throw new Error(errData.error_data.reduce((R, ed) => [...R, ...ed.messages], []));
      }
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

      setIsLoading(false);
      const data = await response.json();
      if (data.ticket) {
        return data.ticket;
      }
      if (data.errorMessage) {
        throw new Error(data.errorMessage);
      }
      throw new Error(`Неочікувана відповідь від серверу: ${data}`);
    };

    const getIdClick = async () => {
      const response = await api.get$('/api/logica/login/', auth);
      if (!response.ok && response.status === 422) {
        const errData = await response.json();
        throw new Error(errData.error_data.reduce((R, ed) => [...R, ...ed.messages], []));
      }
      if (!response.ok) throw new Error(`${response.status} ${response.statusText}`);

      const payload = await response.json();
      const signBase64 = await onSign(payload.stringToSign);
      const { requestId } = payload;
      return getTicketClick(signBase64, requestId);
    };

    setIsLoading(true);
    setError(null);
    getIdClick()
      .then((tck) => setTicket(tck))
      .catch((e) => setError(e.message))
      .finally(() => setIsLoading(false));
  }, [auth, onSign, setTicket]);

  return (
    <div style={{ marginLeft: '15px' }}>
      <LogicaPing available={logicaPingSuccess} handleAvailable={setLogicaPingSuccess} />
      <div
        id="sign-widget-parent"
        style={{
          width: '700px',
          height: '500px',
        }}
      />
      <div id="sign-data-block" style={{ display: 'none' }}>
        {error && (
          <Alert severity="error">
            <AlertTitle>
              Помилка авторизації.
            </AlertTitle>
            {error}
          </Alert>
        )}
        {error === 'Сертифікат не знайдено' && (
          <Alert severity="error">
            <AlertTitle>
              Немає серитифікату.
            </AlertTitle>
            Ви можете завантажити сертифікат з
            {' '}
            <a
              href="http://acsk.treasury.gov.ua/poshuk_sertificativ/"
              target="_blank"
              rel="noopener noreferrer"
            >
              центру сертифікації
            </a>
          </Alert>
        )}

        {ticket && ticket !== 'null' && <h3>Авторизація пройшла успішно</h3>}
        {!error && (!ticket || ticket === 'null') && (
          <Button autoFocus onClick={processHandler} ref={inputRef} disabled={!logicaPingSuccess}>
            {isLoading && (
              <Loader
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
              />
            )}
            Перевірка ЕЦП у &quot;LOGICA&quot;
          </Button>
        )}
      </div>
    </div>
  );
}

Authentication.propTypes = {
  widgetUri: PropTypes.string.isRequired,
};

export default Authentication;

Authentication.propTypes = {
  widgetUri: PropTypes.string,
};

Authentication.defaultProps = {
  widgetUri: 'https://ciat.net.ua',
};
